import EditIcon from '@mui/icons-material/Edit';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { MRT_ColumnDef } from 'mantine-react-table';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';
import { formatCurrencyShort } from '../../../../shared/utils';
import GenericTable, {
    tableBodyCellsStyles,
    tableHeadCellStyles,
} from '../../../shared/GenericTable';
import ExpenseModal from '../../Reservations/ExpenseClaims/ExpenseModal';

export default function ExpenseClaimsList(params: {
    expenses: {
        id: string;
        name: string;
        description: string;
        amount: number;
        date: Date;
        createdBy: {
            id: number;
            email: string;
            firstName: string;
            lastName: string;
        };
        bookingId: number;
        attachments: [
            {
                uuid: string;
                src: string;
                name: string;
            },
        ];
        fullyPaidAt: Date;
        billId?: number;
        invoiceId?: number;
        hostPaidAt?: Date;
    }[];
}) {
    const { expenses } = params;
    const isMobile = useMediaQuery('(max-width: 770px)');
    const [currentIndex, setCurrentIndex] = useState<number | null>(null);
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const theme = useTheme();

    const handleRowClick = (params: any) => {
        const index =
            expenses?.findIndex((value) => value?.id === params?.id) ?? null;
        setCurrentIndex(index);
        setShowExpenseModal(true);
    };

    const columns: MRT_ColumnDef<any>[] = [
        {
            accessorKey: 'id',
            header: 'Expense Id',
            Cell: ({ cell }) => `#${cell.getValue()}`,
            size: 100,
        },
        {
            accessorKey: 'name',
            header: 'Expense',
            size: 140,
        },
        {
            accessorKey: 'date',
            header: 'Date',
            Cell: ({ cell }) =>
                moment(cell.getValue<string>()).format('DD/MM/YYYY'),
            size: 130,
        },
        {
            accessorKey: 'externalSource',
            header: 'External Source',
        },
        {
            accessorFn: (row) =>
                `Reservation #${row.bookingId} - ${row.module?.name}`,
            id: 'bookingId',
            header: 'Reservation',
        },
        {
            accessorFn: (row) => row.guest?.name ?? row.guest?.owner ?? '—',
            id: 'billedId',
            header: 'Guest',
            size: 130,
        },
        {
            accessorKey: 'amount',
            header: 'Amount (inc GST)',
            Cell: ({ cell }) =>
                cell.getValue()
                    ? formatCurrencyShort(cell.getValue<number>())
                    : '$0.00',
            mantineTableHeadCellProps: {
                align: 'right',
                style: { ...tableHeadCellStyles, paddingRight: 30 },
            },
            mantineTableBodyCellProps: {
                align: 'right',
                style: { ...tableBodyCellsStyles, paddingRight: 30 },
            },
        },
        {
            id: 'isPaid',
            accessorFn: (row) =>
                row.hostPaidAt ? 'Paid' : row.isDraft ? 'Draft' : 'Unpaid',
            header: 'Status',
            filterVariant: 'select',
            filterFn: (row, columnId, filterValue) => {
                if (!filterValue) return true;
                const value = row.getValue(columnId); // value will be 'Paid' or 'Unpaid'
                return value === filterValue;
            },
            mantineFilterSelectProps: {
                data: [
                    { label: 'Paid', value: 'Paid' },
                    { label: 'Unpaid', value: 'Unpaid' },
                ],
                clearable: true,
            },
            Cell: ({ cell }) => {
                const value = cell.getValue<string>(); // 'Paid' or 'Unpaid'
                const isPaid = value === 'Paid';
                return (
                    <CustomChip
                        label={
                            <Typography
                                color={isPaid ? 'green' : 'red'}
                                sx={{
                                    fontSize: '12px',
                                }}
                            >
                                {value}
                            </Typography>
                        }
                        color={isPaid ? 'green' : 'red'}
                        variant='outlined'
                    />
                );
            },
        },
        {
            id: 'actions',
            header: isMobile ? '' : 'Actions',
            size: isMobile ? 5 : 60,
            Cell: ({ row }) => (
                <CustomButton
                    sx={{
                        textTransform: 'lowercase',
                        '&:hover': {
                            cursor: 'pointer',
                            color: theme.palette.textAccent.main,
                            backgroundColor: 'transparent',
                            background: 'none',
                        },
                    }}
                >
                    <EditIcon fontSize='small' sx={{ fontSize: '16px' }} />
                </CustomButton>
            ),
        },
    ];

    const processedData = useMemo(() => {
        if (!expenses) return [];
        return expenses;
    }, [expenses]);

    return (
        <Box
            sx={{
                height: 'auto',
                width: isMobile ? '90vw' : '100%',
                paddingTop: '50px',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    overflowX: 'auto',
                }}
            >
                <GenericTable
                    columns={columns}
                    data={processedData}
                    tableName='7DAY-Earnings-Report'
                    loading={!expenses}
                    onRowClick={(row) => handleRowClick(row)}
                    cursor={'pointer'}
                />
            </Box>
            <ExpenseModal
                open={showExpenseModal}
                onClose={() => setShowExpenseModal(false)}
                expenses={expenses}
                isEditing={true}
                index={currentIndex}
            />
        </Box>
    );
}
